// Pure (no DOM): a small Markdown reader for content/SETTER-PLAYBOOK.md, and the map from curriculum items with no
// URL to the place in the app they point at (a playbook section or the Practice tab). Only what the playbook uses:
// headings, paragraphs, bullet and numbered lists (nested by indent), tables, rules, fenced code, block quotes,
// and inline **bold**, *italic*, `code` and [links](url). The output is data; Playbook.tsx renders it as elements,
// so no HTML from the file is ever injected.

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'strong'; c: Inline[] }
  | { t: 'em'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string; c: Inline[] };

export interface ListItem { c: Inline[]; children: Block[] }

export type Block =
  | { t: 'heading'; level: number; c: Inline[]; text: string; id: string }
  | { t: 'para'; c: Inline[] }
  | { t: 'list'; ordered: boolean; start: number; items: ListItem[] }
  | { t: 'table'; head: Inline[][]; rows: Inline[][][] }
  | { t: 'hr' }
  | { t: 'code'; v: string }
  | { t: 'quote'; c: Block[] };

export interface Section { id: string; num: number | null; title: string }

// ---------- inline ----------

const INLINE_RE = /(`[^`]+`)|(\*\*(?:(?!\*\*).)+\*\*)|(\[[^\]]+\]\([^)\s]+\))|(\*[^*\s](?:[^*]*[^*\s])?\*)/;

export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let rest = src;
  while (rest) {
    const m = INLINE_RE.exec(rest);
    if (!m) { out.push({ t: 'text', v: rest }); break; }
    if (m.index > 0) out.push({ t: 'text', v: rest.slice(0, m.index) });
    const tok = m[0];
    if (m[1]) out.push({ t: 'code', v: tok.slice(1, -1) });
    else if (m[2]) out.push({ t: 'strong', c: parseInline(tok.slice(2, -2)) });
    else if (m[3]) {
      const lm = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok)!;
      out.push({ t: 'link', href: lm[2], c: parseInline(lm[1]) });
    } else out.push({ t: 'em', c: parseInline(tok.slice(1, -1)) });
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

export function inlineText(c: Inline[]): string {
  return c.map((i) => (i.t === 'text' || i.t === 'code' ? i.v : inlineText(i.c))).join('');
}

/** Only web links and in-page anchors become links; anything else (javascript:, relative files) stays text. */
export function safeHref(href: string): string | null {
  if (/^#[\w-]+$/.test(href)) return href;
  try {
    const u = new URL(href);
    return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'mailto:' ? u.href : null;
  } catch {
    return null;
  }
}

// ---------- blocks ----------

export function slugify(s: string): string {
  return s.toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'section';
}

const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const isTableSep = (l: string) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => parseInline(c.trim()));

interface RawItem { indent: number; ordered: boolean; num: number; lines: string[]; sub: string[] }

function parseList(lines: string[]): Block[] {
  // Top-level items are those at the smallest indent; deeper lines belong to the item above them.
  const baseIndent = Math.min(...lines.filter((l) => LIST_RE.test(l)).map((l) => LIST_RE.exec(l)![1].length));
  const items: RawItem[] = [];
  for (const l of lines) {
    const m = LIST_RE.exec(l);
    if (m && m[1].length === baseIndent) {
      items.push({ indent: m[1].length, ordered: /\d/.test(m[2]), num: parseInt(m[2], 10) || 1, lines: [m[3]], sub: [] });
    } else if (items.length) {
      const cur = items[items.length - 1];
      if (m || cur.sub.length) cur.sub.push(l);
      else cur.lines.push(l.trim());
    }
  }
  const out: Block[] = [];
  for (const it of items) {
    const last = out[out.length - 1];
    const lead = (s: string) => s.length - s.trimStart().length;
    const subIndent = Math.min(...it.sub.filter((s) => s.trim()).map(lead));
    const item: ListItem = { c: parseInline(it.lines.join(' ')), children: it.sub.length ? parseBlocks(it.sub.map((s) => s.slice(Math.min(lead(s), subIndent))).join('\n')) : [] };
    if (last && last.t === 'list' && last.ordered === it.ordered) last.items.push(item);
    else out.push({ t: 'list', ordered: it.ordered, start: it.num, items: [item] });
  }
  return out;
}

export function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, '\n').split('\n');
  const out: Block[] = [];
  const ids = new Map<string, number>();
  let para: string[] = [];
  const flush = () => {
    if (para.length) out.push({ t: 'para', c: parseInline(para.join(' ')) });
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (!l.trim()) { flush(); continue; }
    if (/^\s*```/.test(l)) {
      flush();
      const body: string[] = [];
      while (++i < lines.length && !/^\s*```/.test(lines[i])) body.push(lines[i]);
      out.push({ t: 'code', v: body.join('\n') });
      continue;
    }
    const h = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(l);
    if (h) {
      flush();
      const c = parseInline(h[2]);
      const text = inlineText(c);
      let id = slugify(text.replace(/^\d+\.\s*/, ''));
      const n = ids.get(id) ?? 0;
      ids.set(id, n + 1);
      if (n) id = `${id}-${n + 1}`;
      out.push({ t: 'heading', level: h[1].length, c, text, id });
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(l)) { flush(); out.push({ t: 'hr' }); continue; }
    if (/^\s*\|/.test(l) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      flush();
      const head = cells(l);
      const rows: Inline[][][] = [];
      i += 1;
      while (i + 1 < lines.length && /^\s*\|/.test(lines[i + 1])) rows.push(cells(lines[++i]));
      out.push({ t: 'table', head, rows });
      continue;
    }
    if (/^\s*>/.test(l)) {
      flush();
      const body: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) body.push(lines[i++].replace(/^\s*>\s?/, ''));
      i--;
      out.push({ t: 'quote', c: parseBlocks(body.join('\n')) });
      continue;
    }
    const lm = LIST_RE.exec(l);
    // A list may interrupt a paragraph (a bullet, or a numbered list starting at 1), as in CommonMark.
    if (lm && (!para.length || !/\d/.test(lm[2]) || parseInt(lm[2], 10) === 1)) {
      flush();
      const body: string[] = [l];
      // A list runs until a blank line followed by something that isn't a list line or an indented continuation.
      while (i + 1 < lines.length) {
        const nx = lines[i + 1];
        if (nx.trim() === '') {
          const after = lines[i + 2];
          if (after !== undefined && (LIST_RE.test(after) || /^\s{2,}\S/.test(after))) { i++; continue; }
          break;
        }
        if (LIST_RE.test(nx) || /^\s+\S/.test(nx) || !/^(#|\||>|```)/.test(nx.trim())) { body.push(nx); i++; continue; }
        break;
      }
      out.push(...parseList(body));
      continue;
    }
    para.push(l.trim());
  }
  flush();
  return out;
}

/** The `##` sections, in order: "4. Gatekeepers" → { id: 'gatekeepers', num: 4, title: 'Gatekeepers' }. */
export function sectionsOf(blocks: Block[]): Section[] {
  return blocks.filter((b): b is Extract<Block, { t: 'heading' }> => b.t === 'heading' && b.level === 2).map((b) => {
    const m = /^(\d+)\.\s*(.*)$/.exec(b.text);
    return { id: b.id, num: m ? parseInt(m[1], 10) : null, title: m ? m[2] : b.text };
  });
}

// ---------- curriculum items without a URL ----------

export type InternalTarget =
  | { tab: 'practice'; label: string }
  | { tab: 'playbook'; section: string | null; label: string };

/** Title keywords → the playbook section (matched by heading) that teaches that item. */
const TOPICS: Array<[RegExp, RegExp]> = [
  [/gatekeeper/i, /gatekeeper/i],
  [/objection/i, /objection/i],
  [/voicemail|\bVM\d?\b/i, /voicemail/i],
  [/\bbook(ing)?\b/i, /booking/i],
  [/game film|\breview\b/i, /game film/i],
  [/disposition/i, /disposition/i],
  [/\bKPIs?\b|target/i, /KPI|target/i],
  [/opener/i, /call structure/i],
  [/dial block/i, /your day/i],
];

/**
 * Where a curriculum item with no URL should point. Role-plays open the Practice tab; the quiz opens the playbook's
 * quiz; "Playbook section N" opens section N; other playbook reading opens the playbook; drills open the section
 * that teaches them. A section that no longer exists falls back to the top of the playbook, never a dead link.
 */
export function internalTarget(item: { kind: string; title: string; why: string }, sections: Section[]): InternalTarget | null {
  if (item.kind === 'roleplay') return { tab: 'practice', label: 'Open Practice' };
  const find = (re: RegExp) => sections.find((s) => re.test(s.title)) ?? null;
  const to = (s: Section | null): InternalTarget => ({ tab: 'playbook', section: s?.id ?? null, label: s ? `Playbook: ${s.num ? `${s.num}. ` : ''}${s.title}` : 'Open the playbook' });
  if (item.kind === 'quiz') {
    const q = find(/quiz/i);
    if (q) return to(q);
  }
  const ref = /playbook section (\d+)/i.exec(`${item.title} ${item.why}`);
  if (ref) {
    const s = sections.find((x) => x.num === parseInt(ref[1], 10));
    if (s) return to(s);
  }
  if (/playbook/i.test(item.title)) return to(null);
  for (const [itemRe, headingRe] of TOPICS) {
    if (itemRe.test(item.title)) return to(find(headingRe));
  }
  return null;
}
